import { create } from 'zustand';

import type { Company, Plant, User } from '@/interfaces';
import { Filter } from '../components/tables/active-companies-table/constants/filters';

export type SelectOption = { id: number; label: string; value: string };

interface SettingsStoreState {
  selectedTab: number;
  setSelectedTab: (tab: number) => void;
  selectedCompany: { id: number; label: string; value: string } | null;
  setSelectedCompany: (company: { id: number; label: string; value: string } | null) => void;
  isOpenCompanyCupsModal: boolean;
  setIsOpenCompanyCupsModal: (isOpen: boolean) => void;
  selectedCups: Plant | null;
  setSelectedCups: (cups: Plant | null) => void;
  isOpenCompanyUsersModal: boolean;
  setIsOpenCompanyUsersModal: (isOpen: boolean) => void;
  selectedCompanyUser: User | null;
  setSelectedCompanyUser: (user: User | null) => void;
  activeCompaniesFilters: Filter[];
  setActiveCompaniesFilters: (filters: Filter[]) => void;
  activeCompanies: Company[];
  setActiveCompanies: (companies: Company[]) => void;
  activeCompaniesFiltered: Company[];
  filterActiveCompanies: () => void;
}

const useSettingsStore = create<SettingsStoreState>((set) => ({
  selectedTab: 0,
  setSelectedTab: (tab) => set({ selectedTab: tab }),
  selectedCompany: null,
  setSelectedCompany: (company) => set({ selectedCompany: company }),
  isOpenCompanyCupsModal: false,
  setIsOpenCompanyCupsModal: (isOpen) => set({ isOpenCompanyCupsModal: isOpen }),
  selectedCups: null,
  setSelectedCups: (cups) => set({ selectedCups: cups }),
  isOpenCompanyUsersModal: false,
  setIsOpenCompanyUsersModal: (isOpen) => set({ isOpenCompanyUsersModal: isOpen }),
  selectedCompanyUser: null,
  setSelectedCompanyUser: (user) => set({ selectedCompanyUser: user }),
  activeCompaniesFilters: [],
  setActiveCompaniesFilters: (filters) => set({ activeCompaniesFilters: filters }),
  activeCompanies: [],
  activeCompaniesFiltered: [],
  setActiveCompanies: (companies) =>
    set((state) => {
      const { activeCompaniesFiltered } = state;

      // update activeCompaniesFiltered only if it does not have some value, to maintain the filters applied
      if (activeCompaniesFiltered.length > 0) return { activeCompanies: companies, activeCompaniesFiltered };
      return { activeCompanies: companies, activeCompaniesFiltered: companies };
    }),
  filterActiveCompanies: () =>
    set((state) => {
      const { activeCompaniesFilters, activeCompanies } = state;
      const companiesFilter = activeCompaniesFilters.find((filter) => filter.accessor === 'name')?.value ?? [];
      const cupsFilter = activeCompaniesFilters.find((filter) => filter.accessor === 'cups')?.value ?? [];
      const aliasFilter = activeCompaniesFilters.find((filter) => filter.accessor === 'alias')?.value ?? [];

      if (companiesFilter.length === 0 && cupsFilter.length === 0 && aliasFilter.length === 0) {
        return { activeCompaniesFiltered: activeCompanies };
      }

      const filtered = activeCompanies.filter((company) => {
        // Filter by company name
        const matchesCompanyName =
          companiesFilter.length === 0 ||
          companiesFilter.some((filter: string) => filter.toLowerCase() === company.name.toLowerCase());

        // Filter by CUPS identifier
        const matchesCups =
          cupsFilter.length === 0 || company.plants.some((plant) => cupsFilter.includes(plant.identifier));

        // Filter by alias
        const matchesAlias =
          aliasFilter.length === 0 || company.plants.some((plant) => aliasFilter.includes(plant.alias));

        return matchesCompanyName && matchesCups && matchesAlias;
      });

      return {
        activeCompaniesFiltered: filtered,
      };
    }),
}));

export default useSettingsStore;
